import React from "react";
import { motion } from "framer-motion";

export default function PrivacyPolicy() {
    const fadeUp = {
        hidden: { opacity: 0, y: 50 },
        visible: { opacity: 1, y: 0, transition: { duration: 0.8 } },
    };

    return (
        <>
            {/* Hero Section */}
            <motion.section
                className="bg-gradient-to-r from-[#0464a4] to-[#34c3e3] text-white text-center py-24 px-4"
                initial="hidden"
                animate="visible"
                variants={fadeUp}
            >
                <h1 className="text-5xl md:text-6xl font-bold mb-4">Privacy Policy</h1>
                <p className="max-w-3xl mx-auto text-lg md:text-xl">
                    Your privacy matters to us. Learn how The Safaiwala collects, uses, and protects your information.
                </p>
            </motion.section>

            {/* Main Content */}
            <section className="max-w-5xl mx-auto px-6 md:px-12 py-20">
                <motion.div
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true }}
                    variants={fadeUp}
                >
                    <h2 className="text-3xl font-bold text-[#e58505] mb-6">
                        Privacy Policy for The Safaiwala
                    </h2>
                 <p className="text-gray-700 mb-2">
  <strong>Effective Date:</strong> 15 October, 2025
</p>

                    <p className="text-gray-700 mb-2">
                        <strong>Company:</strong> The Safai wala
                    </p>
                    <p className="text-gray-700 mb-2">
                        <strong>Website:</strong>{" "}
                        <a
                            href="https://thesafaiwala.com"
                            target="_blank"
                            rel="noreferrer"
                            className="text-[#0464a4] hover:underline"
                        >
                            https://thesafaiwala.com
                        </a>
                    </p>
                    <p className="text-gray-700 mb-8">
                        <strong>Email:</strong>{" "}
                        <a
                            href="mailto: thesafaiwalaglobal@gmail.com"
                            className="text-[#0464a4] hover:underline"
                        >
                            thesafaiwalaglobal@gmail.com
                        </a>
                    </p>

                    {/* Section 1 */}
                    <h3 className="text-2xl font-semibold text-[#0464a4] mb-3">1. Background and Scope</h3>
                    <p className="text-gray-700 mb-6 leading-relaxed">
                        This Privacy Policy applies to users (“you”, “your”) who access or use our website or mobile application to book cleaning services offered by The Safaiwala (“we”, “our”, “us”). By using our services, you consent to the collection and use of your data as outlined in this Policy.
                    </p>

                    {/* Section 2 */}
                    <h3 className="text-2xl font-semibold text-[#0464a4] mb-3">2. Information We Collect</h3>
                    <ul className="list-disc list-inside text-gray-700 mb-6 space-y-2">
                        <li><strong>Contact Information:</strong> name, phone number, email address, and service location.</li>
                        <li><strong>Booking Details:</strong> selected service type, date, time, and preferences.</li>
                        <li><strong>Payment Data:</strong> processed through third-party payment gateways (we never store card details).</li>
                        <li><strong>Device Information:</strong> IP address, browser type, and app usage statistics to improve performance.</li>
                        <li><strong>Communication Data:</strong> messages, feedback, or inquiries you send us.</li>
                    </ul>

                    {/* Section 3 */}
                    <h3 className="text-2xl font-semibold text-[#0464a4] mb-3">3. How We Collect Information</h3>
                    <ul className="list-disc list-inside text-gray-700 mb-6 space-y-2">
                        <li><strong>Directly from you</strong> — when you create an account, make a booking, or contact us.</li>
                        <li><strong>Automatically</strong> — through cookies, analytics tools, and system logs when you use our website or app.</li>
                        <li><strong>From third-party partners</strong> — such as payment processors or communication platforms.</li>
                    </ul>

                    {/* Section 4 */}
                    <h3 className="text-2xl font-semibold text-[#0464a4] mb-3">4. How We Use Your Information</h3>
                    <ul className="list-disc list-inside text-gray-700 mb-6 space-y-2">
                        <li>To process bookings and deliver cleaning services.</li>
                        <li>To send service confirmations, reminders, and updates.</li>
                        <li>To improve our service quality and user experience.</li>
                        <li>To process secure payments and issue receipts.</li>
                        <li>To comply with legal obligations and prevent fraud.</li>
                    </ul>

                    {/* Section 5 */}
                    <h3 className="text-2xl font-semibold text-[#0464a4] mb-3">5. Data Sharing and Disclosure</h3>
                    <p className="text-gray-700 mb-4">
                        We value your privacy and do not sell your data. We only share information with:
                    </p>
                    <ul className="list-disc list-inside text-gray-700 mb-6 space-y-2">
                        <li>Authorized cleaning professionals for completing your booking.</li>
                        <li>Third-party vendors (payment, SMS, or email services) under strict data protection agreements.</li>
                        <li>Regulatory or legal authorities when required by law.</li>
                    </ul>

                    {/* Section 6 */}
                    <h3 className="text-2xl font-semibold text-[#0464a4] mb-3">6. Data Security</h3>
                    <p className="text-gray-700 mb-6 leading-relaxed">
                        We employ secure servers, encryption, and strict access controls to protect your data. Despite our efforts, no digital platform is completely immune from risk.
                    </p>

                    {/* Section 7 */}
                    <h3 className="text-2xl font-semibold text-[#0464a4] mb-3">7. Your Rights and Choices</h3>
                    <p className="text-gray-700 mb-4">
                        You can request access, correction, or deletion of your data. You may also withdraw consent for location or marketing communications anytime through device settings or by emailing us.
                    </p>
                    <p className="text-gray-700 mb-6">
                        For data-related queries, contact us at{" "}
                        <a href="mailto:thesafaiwalaglobal@gmail.com" className="text-[#0464a4] hover:underline">
                            thesafaiwalaglobal@gmail.com
                        </a>.
                    </p>

                    {/* Section 8 */}
                    <h3 className="text-2xl font-semibold text-[#0464a4] mb-3">8. Cookies</h3>
                    <p className="text-gray-700 mb-6 leading-relaxed">
                        Our website uses cookies to enhance your experience, store preferences, and analyze usage. You can disable cookies through your browser, but some parts of our site may not function properly.
                    </p>

                    {/* Section 9 */}
                    <h3 className="text-2xl font-semibold text-[#0464a4] mb-3">9. Data Retention</h3>
                    <p className="text-gray-700 mb-6 leading-relaxed">
                        We retain your data only as long as necessary for providing services, legal compliance, or resolving disputes. Once retention is no longer required, your information will be securely deleted.
                    </p>

                    {/* Section 10 */}
                    <h3 className="text-2xl font-semibold text-[#0464a4] mb-3">10. Updates to This Policy</h3>
                    <p className="text-gray-700 mb-6 leading-relaxed">
                        We may revise this Privacy Policy from time to time. Updated versions will be posted on this page with a revised “Effective Date.”
                    </p>

                    {/* Section 11 */}
                    <h3 className="text-2xl font-semibold text-[#0464a4] mb-3">11. Contact Us</h3>
                    <p className="text-gray-700 leading-relaxed">
                        For privacy concerns, grievances, or feedback, please contact:
                    </p>
                    <ul className="mt-2 text-gray-700">
                        <li>📧 Email: thesafaiwalaglobal@gmail.com</li>
                        <li>📞 Phone: 9111211500</li>
                        <li>🌐 Website: https://thesafaiwala.com</li>
                    </ul>
                </motion.div>
            </section>

            {/* Footer CTA */}
            <section className="bg-[#8e8d50] text-white text-center py-20 px-4">
                <h2 className="text-4xl md:text-5xl font-bold mb-6">
                    We Care About Your Privacy
                </h2>
                <p className="mb-8 max-w-xl mx-auto text-lg">
                    At The Safaiwala, we are committed to protecting your data and ensuring a secure, transparent experience.
                </p>
            </section>
        </>
    );
}
